import React, { useState } from "react";

// ── Admin page: upload NetSuite items CSV ─────────────────────
// Place this component anywhere in your Admin Dashboard
// (e.g., behind a button in the ActionMenu or as a new tab).
//
// HOW TO GET THE CSV FROM NETSUITE:
//   Lists → Accounting → Items
//   Customize → export to CSV
//   The CSV just needs one column: Item Name/Number
//   (other columns are ignored)
//
// Once uploaded, the server keeps the list in memory and the
// depositor form checks new item names against it on blur.
// Re-upload whenever you want to refresh the list.

export default function NetSuiteItemsUpload({ onClose }) {
  const [file, setFile]         = useState(null);
  const [status, setStatus]     = useState(null); // null | "loading" | {count} | {error}
  const [preview, setPreview]   = useState([]);

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setStatus(null);

    const reader = new FileReader();
    reader.onload = (evt) => {
      const lines = evt.target.result.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
      setPreview(lines.slice(0, 10)); // show first 10 lines as preview
    };
    reader.readAsText(f);
  };

  const handleUpload = async () => {
    if (!file) return;
    setStatus("loading");
    try {
      const text = await file.text();
      const res = await fetch("https://dshb-deposits.its.uiowa.edu/api/admin/load-netsuite-csv", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ csvText: text }),
      });

      let data;
      const contentType = res.headers.get("content-type") || "";
      if (contentType.includes("application/json")) {
        data = await res.json();
      } else {
        const raw = await res.text();
        throw new Error(`Server error (${res.status}): ${raw.slice(0, 200)}`);
      }

      if (res.ok && data.success) {
        setStatus({ count: data.count });
      } else {
        setStatus({ error: data.error || "Upload failed" });
      }
    } catch (err) {
      setStatus({ error: err.message });
    }
  };

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
      display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
    }}>
      <div style={{
        background: "#fff", borderRadius: "12px", width: "480px", maxWidth: "95vw",
        boxShadow: "0 20px 60px rgba(0,0,0,0.25)", overflow: "hidden",
      }}>
        {/* Header */}
        <div style={{ background: "#E1E9FC", padding: "18px 22px", borderBottom: "1px solid #D4D8DF", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <h3 style={{ margin: "0 0 2px", fontSize: "16px", fontWeight: 800, color: "#14161B" }}>Load NetSuite Items</h3>
            <p style={{ margin: 0, fontSize: "12px", color: "#305DBD" }}>Upload a CSV of item names to check for duplicates</p>
          </div>
          {onClose && (
            <button onClick={onClose} style={{ background: "none", border: "none", fontSize: "20px", cursor: "pointer", color: "#305DBD" }}>✕</button>
          )}
        </div>

        <div style={{ padding: "22px" }}>
          {/* Instructions */}
          <div style={{ background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: "8px", padding: "12px 14px", marginBottom: "16px", fontSize: "12px", color: "#075985" }}>
            <p style={{ margin: "0 0 6px", fontWeight: 700 }}>How to export from NetSuite:</p>
            <ol style={{ margin: 0, paddingLeft: "18px", lineHeight: 1.7 }}>
              <li>Lists → Accounting → Items</li>
              <li>View all items, then click Export → CSV</li>
              <li>The CSV just needs an "Item Name/Number" column (others ignored)</li>
            </ol>
          </div>

          {/* File picker */}
          <div style={{ marginBottom: "16px" }}>
            <label style={{ display: "block", fontSize: "13px", fontWeight: 600, color: "#14161B", marginBottom: "6px" }}>
              Select CSV file
            </label>
            <input type="file" accept=".csv,.txt" onChange={handleFile}
              style={{ fontSize: "13px" }} />
          </div>

          {/* Preview */}
          {preview.length > 0 && (
            <div style={{ marginBottom: "16px" }}>
              <p style={{ fontSize: "12px", fontWeight: 600, color: "#6b7280", margin: "0 0 6px" }}>
                Preview (first {preview.length} rows):
              </p>
              <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "6px", padding: "8px 12px", fontSize: "12px", fontFamily: "monospace", maxHeight: "120px", overflowY: "auto" }}>
                {preview.map((line, i) => <div key={i} style={{ color: "#374151" }}>{line}</div>)}
              </div>
            </div>
          )}

          {/* Status */}
          {status === "loading" && (
            <p style={{ fontSize: "13px", color: "#6b7280", margin: "0 0 12px" }}>⏳ Uploading…</p>
          )}
          {status?.count !== undefined && (
            <div style={{ background: "#f0fdf4", border: "1px solid #86efac", borderRadius: "6px", padding: "10px 14px", marginBottom: "12px", fontSize: "13px", color: "#15803d", fontWeight: 600 }}>
              ✅ {status.count.toLocaleString()} item names loaded successfully. The form will now check new submissions against this list.
            </div>
          )}
          {status?.error && (
            <div style={{ background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: "6px", padding: "10px 14px", marginBottom: "12px", fontSize: "13px", color: "#dc2626" }}>
              ❌ {status.error}
            </div>
          )}

          {/* Actions */}
          <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
            {onClose && (
              <button onClick={onClose} style={{ padding: "9px 16px", background: "#fff", border: "1px solid #D4D8DF", borderRadius: "7px", fontSize: "13px", cursor: "pointer", color: "#374151" }}>
                Cancel
              </button>
            )}
            <button
              onClick={handleUpload}
              disabled={!file || status === "loading"}
              style={{
                padding: "9px 20px", background: !file || status === "loading" ? "#D4D8DF" : "#305DBD",
                color: "#fff", border: "none", borderRadius: "7px",
                fontSize: "13px", fontWeight: 700,
                cursor: !file || status === "loading" ? "not-allowed" : "pointer",
              }}>
              {status === "loading" ? "Uploading…" : "Upload & Load"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}